/* AttentionInboxCard — single home-page card that aggregates every red /
   amber signal the brand currently has across the platform.

   The small business owner's recurring complaint: "I open WAVE and don't
   know where to start." This card answers that — one list, one place to
   clear it, one action per row.

   Data source: GET /api/brand/attention-inbox — a single backend
   aggregator that fans out to drafts/wallet/data-health (and future
   sources: OAuth-expired, wave-paused, contract-breach) and returns
   them in a stable shape that the mobile app, weekly summary email,
   and SSP surfaces all share.

   Hidden entirely when there are zero signals (clean inbox = no card).
*/
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ListChecks } from 'lucide-react';
import { UrgencyBadge } from '../ui/urgency-badge';

const API = process.env.REACT_APP_BACKEND_URL;

function useAttentionInbox() {
  const [inbox, setInbox] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/brand/attention-inbox`, { withCredentials: true });
        if (!cancelled) setInbox(data);
      } catch {
        if (!cancelled) setInbox({ signals: [], counts: { critical: 0, warning: 0, total: 0 }, highest_level: null });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { inbox, loaded };
}

export default function AttentionInboxCard() {
  const { inbox, loaded } = useAttentionInbox();

  if (!loaded || !inbox || !inbox.signals || inbox.signals.length === 0) return null;

  const signals = inbox.signals;
  const highest = inbox.highest_level || 'warning';

  return (
    <div
      data-testid="attention-inbox-card"
      data-signal-count={signals.length}
      data-highest-level={highest}
      className="rounded-2xl p-4 lg:p-5"
      style={{ background: '#0F1014', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-gray-400" />
          <h3 className="text-[11px] uppercase tracking-[0.18em] font-black text-gray-400">
            Needs your attention
          </h3>
          <UrgencyBadge
            level={highest}
            label={`${signals.length}`}
            icon={false}
          />
        </div>
        <span className="text-[10px] text-gray-600">One list, one place to clear it.</span>
      </div>

      <ul className="space-y-1.5">
        {signals.map(sig => (
          <li key={sig.id}>
            <Link
              to={sig.to}
              data-testid={`attention-row-${sig.id}`}
              data-level={sig.level}
              data-source={sig.source}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all hover:bg-white/[0.03]"
              style={{ border: '1px solid rgba(255,255,255,0.04)' }}
            >
              <UrgencyBadge level={sig.level} label={sig.badge} icon={false} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-bold text-white truncate">{sig.title}</p>
                {sig.body && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{sig.body}</p>}
              </div>
              <span className="text-[10px] font-bold text-gray-400 flex items-center gap-0.5 whitespace-nowrap">
                {sig.cta} <ChevronRight className="h-3 w-3" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
